/**
 * Pure helpers for the auth doors (seam 2 of the UI redesign). The doors
 * drive Clerk's API themselves (`useSignIn` / `useSignUp`); these functions
 * are the parts of that flow with no React and no network in them: picking
 * the email-code factor out of what Clerk offers, deciding where a door sends
 * the person afterwards, the OAuth round-trip's two URLs, and one message out
 * of a Clerk error.
 *
 * The shapes mirror the pinned SDK (`@clerk/tanstack-react-start` →
 * `@clerk/clerk-react` → `@clerk/shared` types): a first factor is
 * `{ strategy, emailAddressId, safeIdentifier }` for `email_code`, and an API
 * error carries `errors: [{ code, message, longMessage? }]`. They are typed
 * structurally here so the seam never imports a transitive package.
 *
 * @since 1.0.0
 */
import { Option } from "effect";

import type { LoginSearch } from "./login-search.ts";

/** The first-factor strategy both doors use. @since 1.0.0 */
export const DOOR_STRATEGY = "email_code" as const;

/** The OAuth strategy the instance enables. @since 1.0.0 */
export const GOOGLE_STRATEGY = "oauth_google" as const;

/** Where a door lands a person when the search names nowhere. @since 1.0.0 */
export const DOOR_HOME = "/orca";

/** A first factor as Clerk lists it (`signIn.supportedFirstFactors`), structurally. @since 1.0.0 */
export interface FirstFactorLike {
  readonly emailAddressId?: string;
  readonly safeIdentifier?: string;
  readonly strategy: string;
}

/** The email-code factor, when Clerk offers one. @since 1.0.0 */
export interface EmailCodeFactorLike {
  readonly emailAddressId: string;
  readonly safeIdentifier: string;
  readonly strategy: typeof DOOR_STRATEGY;
}

/**
 * The email-code factor out of the factors Clerk offers after
 * `signIn.create({ identifier })` — the one the door prepares. None when
 * the instance offers no such factor for this identifier.
 *
 * @since 1.0.0
 */
export const emailCodeFactor = (
  factors: ReadonlyArray<FirstFactorLike> | null | undefined,
): Option.Option<EmailCodeFactorLike> =>
  Option.fromNullable(
    factors?.find((factor): factor is FirstFactorLike & { readonly emailAddressId: string } =>
      factor.strategy === DOOR_STRATEGY && factor.emailAddressId !== undefined
    ),
  ).pipe(
    Option.map((factor) => ({
      strategy: DOOR_STRATEGY,
      emailAddressId: factor.emailAddressId,
      safeIdentifier: factor.safeIdentifier ?? "",
    })),
  );

/** Only a same-origin path is honoured as a redirect; anything else goes home. */
const isLocalPath = (value: string): boolean => value.startsWith("/") && !value.startsWith("//");

/** Where the door sends the person once signed in. @since 1.0.0 */
export const doorRedirect = (search: LoginSearch): string =>
  search.redirect_url !== undefined && isLocalPath(search.redirect_url) ? search.redirect_url : DOOR_HOME;

/** The two URLs Clerk's OAuth redirect needs, for one door. @since 1.0.0 */
export interface OAuthRedirectUrls {
  /** Where the provider sends the browser back: the door's callback route. */
  readonly redirectUrl: string;
  /** Where the callback sends the person once the session exists. */
  readonly redirectUrlComplete: string;
}

/** See {@link OAuthRedirectUrls}. @since 1.0.0 */
export const oauthRedirectUrls = (door: "login" | "sign-up", search: LoginSearch): OAuthRedirectUrls => ({
  redirectUrl: `/${door}/sso-callback`,
  redirectUrlComplete: doorRedirect(search),
});

/** A Clerk API error's shape, structurally. @since 1.0.0 */
export interface ClerkErrorLike {
  readonly errors?: ReadonlyArray<{ readonly code?: string; readonly longMessage?: string; readonly message?: string }>;
  readonly message?: string;
}

const isClerkErrorLike = (error: unknown): error is ClerkErrorLike => typeof error === "object" && error !== null;

/** The fallback when an error says nothing a person can act on. @since 1.0.0 */
export const DOOR_ERROR_FALLBACK = "Something went wrong. Try again.";

/**
 * One line a person can act on, out of whatever Clerk threw: the first API
 * error's long message, then its message, then the error's own message.
 *
 * @since 1.0.0
 */
export const clerkErrorMessage = (error: unknown): string => {
  if (!isClerkErrorLike(error)) return DOOR_ERROR_FALLBACK;
  const first = error.errors?.[0];
  const fromApi = first?.longMessage ?? first?.message;
  if (fromApi !== undefined && fromApi !== "") return fromApi;
  return error.message !== undefined && error.message !== "" ? error.message : DOOR_ERROR_FALLBACK;
};

/** Six digits, as the code field accepts them. @since 1.0.0 */
export const isCompleteCode = (code: string): boolean => /^\d{6}$/.test(code);
